import { ReactNode } from 'react';

import DetectRTC from 'detectrtc';
import { useShallow } from 'zustand/react/shallow';

import useVirtualBackgroundHandler from '@src/hooks/virtualBackground/useVirtualBackgroundHandler';
import useVirtualBackgroundConfig from '@src/hooks/virtualBackground/useVirtualBackgroundConfig';
import { useVirtualBackgroundStore } from '@src/stores/virtualBackground.store';
import { useVideoStreamStore } from '@src/stores';

interface IVirtualBackgroundProviderProps {
  children: ReactNode;
}

/**
 * All virtual background processing happens here, and components consume
 * the processed stream from useVideoStreamStore.
 *
 * Benefits:
 * - Single processing loop (no duplicate work)
 * - Reduced CPU usage
 * - No stream conflicts
 * - Single source of truth for VB state
 */
export const VirtualBackgroundProvider: React.FC<IVirtualBackgroundProviderProps> = ({ children }) => {
  const { appliedEffect, selectedEffect } = useVirtualBackgroundStore(
    useShallow((state) => ({
      appliedEffect: state.appliedEffect,
      selectedEffect: state.selectedEffect,
    }))
  );

  const { originalStream } = useVideoStreamStore(
    useShallow((state) => ({
      originalStream: state.originalStream,
    }))
  );

  // CRITICAL: Watch config changes and update worker
  useVirtualBackgroundConfig();

  // CRITICAL: This is the ONLY place useVirtualBackgroundHandler should be called
  // All other components should read from stores, not create their own instances
  // CRITICAL: hasActiveEffect is true if EITHER appliedEffect OR selectedEffect is not 'none'
  // This ensures the worker runs for preview even if applied effect is 'none'
  const hasActiveEffect = appliedEffect.type !== 'none' || selectedEffect.type !== 'none';
  //  Only process if we have an active effect AND a live camera stream
  // This ensures worker is released when camera is closed, even if effect is selected
  const isReadyToProcess = hasActiveEffect && !!originalStream;

  useVirtualBackgroundHandler({
    isReadyToProcess,
    instanceId: 'GLOBAL_VB_PROVIDER',
    isMobileDevice: DetectRTC.isMobileDevice,
  });

  return <>{children}</>;
};
